import { Link } from "react-router-dom";
import { CalendarPlus } from "lucide-react";

import { ComicPanel } from "@/components/comic";

import { MeetingListRow } from "./MeetingListRow";
import {
  addDays,
  meetingPivot,
  ReschedulePick,
  sameDay,
  startOfWeek,
  UiMeeting,
} from "./shared";

/**
 * Chronological list grouped by Today / Tomorrow / Later this week /
 * Upcoming / Earlier. Each bucket is its own comic panel.
 */
export function MeetingsListView({
  meetings,
  now,
  onCancel,
  onReschedule,
  onAbandonReschedule,
  onResolve,
  onOpenNegotiation,
}: {
  meetings: UiMeeting[];
  now: Date;
  onCancel?: (id: string) => void;
  onReschedule?: (id: string, pick: ReschedulePick) => void;
  onAbandonReschedule?: (id: string) => void;
  onOpenNegotiation?: (id: string) => void;
  onResolve?: (id: string, startTimes: string[]) => void;
}) {
  const groups = groupMeetings(meetings, now);
  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-3xl border border-border bg-gradient-to-br from-muted/40 to-background px-6 py-12 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary">
          <CalendarPlus className="h-6 w-6" />
        </span>
        <div className="space-y-1">
          <p className="font-display text-sm font-semibold text-foreground">
            No meetings here yet
          </p>
          <p className="max-w-xs text-xs text-muted-foreground">
            Ask your agent to find a time. It handles the back-and-forth and lands
            the result right here.
          </p>
        </div>
        <Link
          to="/"
          className="rounded-full bg-primary px-4 py-1.5 font-display text-xs font-semibold text-primary-foreground shadow-sm transition hover:brightness-110"
        >
          Schedule something
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <ComicPanel key={g.label} title={g.label}>
          <div className="space-y-2">
            {g.meetings.map((m) => (
              <MeetingListRow
                key={m.id}
                meeting={m}
                onCancel={onCancel}
                onReschedule={onReschedule}
                onAbandonReschedule={onAbandonReschedule}
                onOpenNegotiation={onOpenNegotiation}
                onResolve={onResolve}
              />
            ))}
          </div>
        </ComicPanel>
      ))}
    </div>
  );
}

function groupMeetings(
  meetings: UiMeeting[],
  now: Date,
): { label: string; meetings: UiMeeting[] }[] {
  const today = now;
  const tomorrow = addDays(now, 1);
  const weekEnd = addDays(startOfWeek(now), 7);

  const sorted = [...meetings].sort(
    (a, b) => meetingPivot(a).getTime() - meetingPivot(b).getTime(),
  );

  const buckets = new Map<string, UiMeeting[]>();
  const order: string[] = [];
  const push = (label: string, m: UiMeeting) => {
    if (!buckets.has(label)) {
      buckets.set(label, []);
      order.push(label);
    }
    buckets.get(label)!.push(m);
  };

  for (const m of sorted) {
    const when = meetingPivot(m);
    let label: string;
    if (sameDay(when, today)) label = "Today";
    else if (sameDay(when, tomorrow)) label = "Tomorrow";
    else if (when > today && when < weekEnd) label = "Later this week";
    else if (when > today) label = "Upcoming";
    else label = "Earlier";
    push(label, m);
  }

  return order.map((label) => ({ label, meetings: buckets.get(label)! }));
}
